import React, { useEffect, useState } from 'react'
import { IonContent, IonPage } from '@ionic/react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, CalendarClock, Loader2, MapPin, Sparkles } from 'lucide-react'
import {
  CreditBalanceDto,
  ProblemDetails,
  RecalculateBirthRecordDto,
  RecalculateBirthRecordResponseDto,
  SubscriptionAccessSnapshotDto,
  type SubscriptionAccessSnapshotDto as SubscriptionAccessSnapshotDtoType,
  type UserDto,
} from '../api'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BirthDateInput } from '../components/ui/birth-date-input'
import { TimeInput } from '../components/ui/time-input'
import { type BirthPlaceSuggestion, searchBirthPlaceSuggestions } from '../features/birthPlaceSearch'
import { notifyBirthRecordUpdated } from '../features/birthRecordRefresh'
import {
  type BirthDateDraft,
  EMPTY_BIRTH_DATE_DRAFT,
  formatCivilBirthDateTime,
  isEmptyBirthDateDraft,
  maxBirthDate,
  parseBirthDate,
  parseBirthTime,
  toBirthDateDraft,
} from '../helpers/birthDateTime'
import { buildUtcDatePreservingClock } from '../helpers/time'
import { HttpError } from '../http'
import useAstroApiClient from '../hooks/useAstroApiClient'

type BirthRecordForm = {
  birthDate: BirthDateDraft
  birthTime: string
  birthPlace: string
  birthLatitude?: number
  birthLongitude?: number
}

type DetailRowProps = {
  label: string
  value: React.ReactNode
}

const EMPTY_FORM: BirthRecordForm = {
  birthDate: EMPTY_BIRTH_DATE_DRAFT,
  birthTime: '',
  birthPlace: '',
}

const createBirthRecordUpdateIdempotencyKey = () => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID()
  }

  return `birth-record-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
  <div className="border-t border-stone-100 py-3 sm:grid sm:grid-cols-[170px_minmax(0,1fr)] sm:gap-4">
    <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</div>
    <div className="mt-1 text-sm text-slate-800 sm:mt-0">{value}</div>
  </div>
)

const getProblemNumber = (problem: ProblemDetails, key: string) => {
  const value = problem[key]
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  return undefined
}

const mapUserToForm = (user: UserDto | null): BirthRecordForm => {
  // GR-027: the stored birthDateTime is a UTC instant. The form starts from the civil
  // date and time the API derived for the birth place, so saving without changes keeps
  // the record; without them the fields start empty instead of guessing a value.
  const birthDate = toBirthDateDraft(user?.birthLocalDate)
  return {
    birthDate,
    birthTime: isEmptyBirthDateDraft(birthDate) ? '' : user?.birthLocalTime ?? '',
    birthPlace: user?.birthPlace ?? '',
    birthLatitude: user?.latitude ?? undefined,
    birthLongitude: user?.longitude ?? undefined,
  }
}

const BirthRecordUpdatePage: React.FC = () => {
  const history = useHistory()
  const { t, i18n } = useTranslation()
  const api = useAstroApiClient()

  const [user, setUser] = useState<UserDto | null>(null)
  const [subscriptionAccess, setSubscriptionAccess] = useState<SubscriptionAccessSnapshotDto | null>(null)
  const [form, setForm] = useState<BirthRecordForm>(EMPTY_FORM)
  const [birthPlaceQuery, setBirthPlaceQuery] = useState('')
  const [birthPlaceSuggestions, setBirthPlaceSuggestions] = useState<BirthPlaceSuggestion[]>([])
  const [birthPlaceError, setBirthPlaceError] = useState<string | null>(null)
  const [isSearchingBirthPlace, setIsSearchingBirthPlace] = useState(false)
  const [showBirthErrors, setShowBirthErrors] = useState(false)
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitResult, setSubmitResult] = useState<RecalculateBirthRecordResponseDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)

  const updateCreditBalanceSnapshot = (overrides: {
    remainingCredits?: number
    includedCredits?: number
    usedCredits?: number
  }) => {
    setSubscriptionAccess(current => {
      if (!current) {
        return current
      }

      return new SubscriptionAccessSnapshotDto({
        ...current,
        credits: new CreditBalanceDto({
          includedCredits: overrides.includedCredits ?? current.credits?.includedCredits,
          usedCredits: overrides.usedCredits ?? current.credits?.usedCredits,
          remainingCredits: overrides.remainingCredits ?? current.credits?.remainingCredits,
          cycleStartsAtUtc: current.credits?.cycleStartsAtUtc,
          cycleEndsAtUtc: current.credits?.cycleEndsAtUtc,
        }),
      }) as SubscriptionAccessSnapshotDtoType
    })
  }

  const reloadBirthRecordContext = React.useCallback(async (activeRef?: { current: boolean }) => {
    const [account, access] = await Promise.all([
      api.apiUsersAuth(),
      api.apiSubscriptionAccess().catch(() => null),
    ])

    if (activeRef && !activeRef.current) {
      return
    }

    setUser(account)
    setSubscriptionAccess(access)
    setForm(mapUserToForm(account))
    setShowBirthErrors(false)
    setBirthPlaceQuery('')
    setBirthPlaceSuggestions([])
    setBirthPlaceError(null)
  }, [api])

  useEffect(() => {
    const activeRef = { current: true }

    const load = async () => {
      setLoading(true)
      setLoadFailed(false)

      try {
        await reloadBirthRecordContext(activeRef)
        setSubmitError(null)
        setSubmitResult(null)
      } catch {
        if (!activeRef.current) {
          return
        }

        setLoadFailed(true)
      } finally {
        if (activeRef.current) {
          setLoading(false)
        }
      }
    }

    void load()

    return () => {
      activeRef.current = false
    }
  }, [reloadBirthRecordContext])

  useEffect(() => {
    const query = birthPlaceQuery.trim()
    if (query.length < 3) {
      setBirthPlaceSuggestions([])
      setBirthPlaceError(null)
      setIsSearchingBirthPlace(false)
      return
    }

    let cancelled = false
    const timeoutId = window.setTimeout(() => {
      setIsSearchingBirthPlace(true)
      setBirthPlaceError(null)

      searchBirthPlaceSuggestions(api, query)
        .then(results => {
          if (!cancelled) {
            setBirthPlaceSuggestions(results)
          }
        })
        .catch(() => {
          if (!cancelled) {
            setBirthPlaceSuggestions([])
            setBirthPlaceError(t('mapaAstralLocationSearchError'))
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsSearchingBirthPlace(false)
          }
        })
    }, 350)

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [api, birthPlaceQuery, t])

  useEffect(() => {
    if (!submitError && !submitResult) {
      return
    }

    setSubmitError(null)
    setSubmitResult(null)
  }, [
    form.birthDate.day,
    form.birthDate.month,
    form.birthDate.year,
    form.birthLongitude,
    form.birthPlace,
    form.birthLatitude,
    form.birthTime,
  ])

  // Birth values are civil strings of the birth place; see formatCivilBirthDateTime.
  const formatBirth = (date?: string | null, time?: string | null) =>
    formatCivilBirthDateTime(date, time, i18n.language) ?? t('notAvailable')

  const formatCoordinates = () => {
    if (typeof user?.latitude !== 'number' || typeof user?.longitude !== 'number') {
      return t('notAvailable')
    }

    return `${user.latitude.toFixed(6)}, ${user.longitude.toFixed(6)}`
  }

  const formatCreditAmount = (value?: number | null) => {
    if (typeof value !== 'number') {
      return t('notAvailable')
    }

    return value.toLocaleString(i18n.language)
  }

  const birthRecordUpdateCost = subscriptionAccess?.creditCosts?.find(cost => {
    const reason = cost.reason as unknown as string | null | undefined
    return reason === 'UpdateBirthRecord'
  })?.amount

  const birthRecordUpdateCostLabel =
    typeof birthRecordUpdateCost === 'number'
      ? t('accountBirthRecordUpdateCostBadge', {
          amount: formatCreditAmount(birthRecordUpdateCost),
        })
      : t('accountBirthRecordUpdateCostUnavailable')
  const remainingCredits = subscriptionAccess?.credits?.remainingCredits
  const hasSufficientCredits =
    typeof birthRecordUpdateCost === 'number' && typeof remainingCredits === 'number'
      ? remainingCredits >= birthRecordUpdateCost
      : true
  const hasKnownCreditBalance =
    typeof birthRecordUpdateCost === 'number' && typeof remainingCredits === 'number'
  const insufficientCreditsMessage = hasKnownCreditBalance && !hasSufficientCredits
    ? t('accountBirthRecordInsufficientCreditsError', {
        requiredCredits: formatCreditAmount(birthRecordUpdateCost),
        remainingCredits: formatCreditAmount(remainingCredits),
      })
    : null

  const safeAreaTop = 'var(--ion-safe-area-top, env(safe-area-inset-top))'
  const safeAreaBottom = 'var(--ion-safe-area-bottom, env(safe-area-inset-bottom))'
  const hasResolvedBirthPlace =
    typeof form.birthLatitude === 'number' && typeof form.birthLongitude === 'number'
  // Both values are required, keeping the limits the endpoint already imposes, plus
  // the single boundary shared by every flow: a birth date is never in the future.
  const today = maxBirthDate()
  const birthDateResult = parseBirthDate(form.birthDate, { required: true, max: today })
  const birthTimeResult = parseBirthTime(form.birthTime, true)
  const hasBirthValues = birthDateResult.status === 'valid' && birthTimeResult.status === 'valid'
  const canReviewChanges =
    hasBirthValues &&
    form.birthPlace.trim().length > 0 &&
    hasResolvedBirthPlace
  const pendingBirthDateTime = hasBirthValues
    ? buildUtcDatePreservingClock(birthDateResult.value, birthTimeResult.value)
    : null

  const handleBirthPlaceSelect = (suggestion: BirthPlaceSuggestion) => {
    setForm(current => ({
      ...current,
      birthPlace: suggestion.displayName,
      birthLatitude: suggestion.latitude,
      birthLongitude: suggestion.longitude,
    }))
    setBirthPlaceQuery('')
    setBirthPlaceSuggestions([])
    setBirthPlaceError(null)
  }

  const getErrorMessage = (error: unknown) => {
    if (error instanceof ProblemDetails) {
      const code = typeof error.code === 'string' ? error.code : undefined
      const requiredCredits = getProblemNumber(error, 'requiredCredits')
      const remainingCreditsFromProblem = getProblemNumber(error, 'remainingCredits')
      const includedCredits = getProblemNumber(error, 'includedCredits')
      const usedCredits = getProblemNumber(error, 'usedCredits')

      if (typeof remainingCreditsFromProblem === 'number') {
        updateCreditBalanceSnapshot({
          remainingCredits: remainingCreditsFromProblem,
          includedCredits,
          usedCredits,
        })
      }

      if (error.status === 402 || code === 'birth-record.insufficient-credits') {
        return t('accountBirthRecordInsufficientCreditsError', {
          requiredCredits: formatCreditAmount(requiredCredits),
          remainingCredits: formatCreditAmount(remainingCreditsFromProblem),
        })
      }

      if (error.status === 400 || code === 'birth-record.invalid-birth-record') {
        return t('accountBirthRecordInvalidRecordError')
      }

      return error.detail ?? error.title ?? t('accountBirthRecordSubmitError')
    }

    if (error instanceof HttpError) {
      if (error.status === 401) {
        return t('accountBirthRecordSubmitUnauthorized')
      }

      return error.message
    }

    if (error instanceof Error && error.message) {
      return error.message
    }

    return t('accountBirthRecordSubmitError')
  }

  const handleConfirmSubmission = async () => {
    if (!pendingBirthDateTime || !hasResolvedBirthPlace || isSubmitting) {
      return
    }

    const birthLatitude = form.birthLatitude
    const birthLongitude = form.birthLongitude
    if (typeof birthLatitude !== 'number' || typeof birthLongitude !== 'number') {
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const result = await api.apiUsersBirthRecordRecalculate(
        createBirthRecordUpdateIdempotencyKey(),
        new RecalculateBirthRecordDto({
          birthDateTime: pendingBirthDateTime,
          birthPlace: form.birthPlace.trim(),
          birthLatitude,
          birthLongitude,
        }),
      )

      await reloadBirthRecordContext().catch(() => undefined)

      updateCreditBalanceSnapshot({
        remainingCredits: result.remainingCredits,
      })
      notifyBirthRecordUpdated()
      setSubmitResult(result)
      setIsConfirmDialogOpen(false)
    } catch (error) {
      setSubmitError(getErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <IonPage>
      <IonContent fullscreen className="bg-gradient-to-br from-stone-50 via-amber-50 to-action-50">
        <div
          className="min-h-screen"
          style={{
            minHeight: '100dvh',
            paddingBottom: `max(0.75rem, ${safeAreaBottom})`,
          }}
        >
          <div
            className="sticky top-0 z-30 border-b border-stone-200 bg-white/90 backdrop-blur-sm"
            style={{
              paddingTop: `max(0.5rem, ${safeAreaTop})`,
            }}
          >
            <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-action-700">
                  {t('accountBirthRecordUpdateTitle')}
                </p>
                <h1 className="truncate text-lg font-semibold text-slate-900 md:text-2xl">
                  {t('accountBirthRecordUpdateAction')}
                </h1>
                <p className="mt-1 hidden text-sm text-slate-600 sm:block">
                  {t('accountBirthRecordUpdatePageSubtitle')}
                </p>
              </div>
              <Button
                variant="outline"
                className="border-stone-300 bg-white/80 text-slate-700 hover:bg-stone-50"
                onClick={() => history.goBack()}
              >
                <ArrowLeft className="h-4 w-4" />
                {t('goBack')}
              </Button>
            </div>
          </div>

          <main className="mx-auto max-w-5xl px-4 py-5 md:py-8">
            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-800 shadow-sm">
              {t('accountBirthRecordUpdatePageIntro')}
            </div>

            {submitResult ? (
              <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50/90 px-4 py-3 text-sm text-emerald-800 shadow-sm">
                <p className="font-semibold">{t('accountBirthRecordSubmitSuccess')}</p>
                <p className="mt-1">
                  {t('accountBirthRecordSubmitSuccessMeta', {
                    debitedCredits: formatCreditAmount(submitResult.debitedCredits),
                    remainingCredits: formatCreditAmount(submitResult.remainingCredits),
                  })}
                </p>
              </div>
            ) : null}

            {loading ? (
              <Card className="border-stone-200 bg-white/95">
                <CardContent className="py-8 text-sm text-slate-600">{t('loading')}</CardContent>
              </Card>
            ) : loadFailed || !user ? (
              <Card className="border-red-200 bg-white/95">
                <CardContent className="py-8 text-sm text-red-600">{t('accountLoadError')}</CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 lg:grid-cols-[0.95fr_1.05fr]">
                <Card className="border-stone-200 bg-white/95 shadow-sm">
                  <CardHeader className="gap-3">
                    <CardTitle className="flex items-center gap-2 text-slate-900">
                      <CalendarClock className="h-5 w-5 text-amber-500" />
                      {t('accountBirthRecordCurrentTitle')}
                    </CardTitle>
                    <CardDescription>{t('accountBirthRecordCurrentDescription')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <DetailRow label={t('birthDate')} value={formatBirth(user.birthLocalDate, user.birthLocalTime)} />
                    <DetailRow label={t('birthPlace')} value={user.birthPlace || t('notAvailable')} />
                    <DetailRow label={t('accountCoordinates')} value={formatCoordinates()} />
                    <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/80 px-4 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">
                            {t('accountBirthRecordUpdateTitle')}
                          </p>
                          <p className="mt-1 text-xs text-slate-600">
                            {t('accountBirthRecordUpdateDescription')}
                          </p>
                        </div>
                        <Badge variant="outline" className="border-emerald-300 bg-white text-emerald-700">
                          {birthRecordUpdateCostLabel}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-stone-200 bg-white/95 shadow-sm">
                  <CardHeader className="gap-3">
                    <CardTitle className="flex items-center gap-2 text-slate-900">
                      <MapPin className="h-5 w-5 text-action-500" />
                      {t('accountBirthRecordFormTitle')}
                    </CardTitle>
                    <CardDescription>{t('accountBirthRecordFormDescription')}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {/* The person's own record, so the birthday autofill hints apply. */}
                      <BirthDateInput
                        id="birth-record-update-date"
                        required
                        max={today}
                        autoComplete="birthday"
                        showErrors={showBirthErrors}
                        value={form.birthDate}
                        onValueChange={(draft) => {
                          setForm(current => ({ ...current, birthDate: draft }))
                        }}
                      />

                      <TimeInput
                        id="birth-record-update-time"
                        required
                        showErrors={showBirthErrors}
                        value={form.birthTime}
                        onValueChange={(draft) => {
                          setForm(current => ({ ...current, birthTime: draft }))
                        }}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="birth-record-update-place">{t('birthPlace')}</Label>
                      <div className="space-y-2">
                        <Input
                          id="birth-record-update-place"
                          type="text"
                          value={form.birthPlace}
                          placeholder={t('mapaAstralBirthPlacePlaceholder')}
                          onChange={(event) => {
                            const value = event.target.value
                            setForm(current => ({
                              ...current,
                              birthPlace: value,
                              birthLatitude: undefined,
                              birthLongitude: undefined,
                            }))
                            setBirthPlaceQuery(value)
                          }}
                        />

                        {form.birthLatitude !== undefined && form.birthLongitude !== undefined && (
                          <div className="flex items-center gap-2 rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-2 text-xs text-cyan-800">
                            <MapPin className="h-3.5 w-3.5" />
                            {t('mapaAstralCoordinates', {
                              lat: form.birthLatitude.toFixed(4),
                              lon: form.birthLongitude.toFixed(4),
                            })}
                          </div>
                        )}

                        {birthPlaceQuery.trim().length >= 3 && (
                          <div className="space-y-2">
                            {isSearchingBirthPlace && (
                              <p className="flex items-center gap-2 text-xs text-slate-500">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                {t('mapaAstralSearchingLocations')}
                              </p>
                            )}

                            {birthPlaceError && (
                              <p className="text-xs text-red-500">{birthPlaceError}</p>
                            )}

                            {!isSearchingBirthPlace && !birthPlaceError && birthPlaceSuggestions.length > 0 && (
                              <div className="max-h-48 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-sm">
                                {birthPlaceSuggestions.map(suggestion => (
                                  <button
                                    key={suggestion.id}
                                    type="button"
                                    className="flex w-full items-start justify-between gap-3 border-b border-slate-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-action-50"
                                    onClick={() => handleBirthPlaceSelect(suggestion)}
                                  >
                                    <span className="block text-slate-800">{suggestion.displayName}</span>
                                    <span className="shrink-0 text-xs text-slate-500">
                                      {suggestion.latitude.toFixed(2)}, {suggestion.longitude.toFixed(2)}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-dashed border-stone-300 bg-stone-50 px-4 py-4">
                      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                        <Sparkles className="h-4 w-4 text-amber-500" />
                        {t('accountBirthRecordImpactTitle')}
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-slate-600">{t('accountBirthRecordImpactDescription')}</p>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="border-amber-300 bg-white text-amber-700">
                          {birthRecordUpdateCostLabel}
                        </Badge>
                        <Badge variant="outline" className="border-slate-300 bg-white text-slate-700">
                          {t('accountBirthRecordAvailableCreditsBadge', {
                            amount: formatCreditAmount(remainingCredits),
                          })}
                        </Badge>
                        <span className="text-xs text-slate-500">{t('accountBirthRecordImpactCost')}</span>
                      </div>
                      <div className="mt-3 space-y-2 text-sm text-slate-700">
                        <p>{t('accountBirthRecordImpactNatalChart')}</p>
                        <p>{t('accountBirthRecordImpactDaily')}</p>
                        <p>{t('accountBirthRecordImpactWeekly')}</p>
                        <p>{t('accountBirthRecordImpactMonthly')}</p>
                      </div>
                    </div>

                    {insufficientCreditsMessage ? (
                      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {insufficientCreditsMessage}
                      </div>
                    ) : null}

                    {submitError ? (
                      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {submitError}
                      </div>
                    ) : null}

                    <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        className="border-stone-300 bg-white text-slate-700 hover:bg-stone-50"
                        onClick={() => history.goBack()}
                      >
                        {t('cancel')}
                      </Button>
                      <Button
                        type="button"
                        disabled={
                          form.birthPlace.trim().length === 0 ||
                          !hasResolvedBirthPlace ||
                          !hasSufficientCredits ||
                          isSubmitting
                        }
                        onClick={() => {
                          if (!canReviewChanges) {
                            setShowBirthErrors(true)
                            return
                          }

                          setIsConfirmDialogOpen(true)
                        }}
                      >
                        {t('accountBirthRecordReviewAction')}
                      </Button>
                    </div>

                    {!hasResolvedBirthPlace && form.birthPlace.trim().length > 0 ? (
                      <p className="text-xs text-slate-500">{t('accountBirthRecordLocationSelectionHint')}</p>
                    ) : null}
                  </CardContent>
                </Card>
              </div>
            )}
          </main>
        </div>
      </IonContent>

      <AlertDialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('accountBirthRecordConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('accountBirthRecordConfirmDescription')}</AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4">
            <div className="rounded-xl border border-stone-200 bg-stone-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-slate-900">{t('accountBirthRecordConfirmSummaryTitle')}</p>
                <Badge variant="outline" className="border-amber-300 bg-white text-amber-700">
                  {birthRecordUpdateCostLabel}
                </Badge>
              </div>
              <div className="mt-3 space-y-2 text-sm text-slate-700">
                <p>
                  <strong>{t('birthDate')}:</strong>{' '}
                  {hasBirthValues ? formatBirth(birthDateResult.value, birthTimeResult.value) : t('notAvailable')}
                </p>
                <p>
                  <strong>{t('birthPlace')}:</strong> {form.birthPlace || t('notAvailable')}
                </p>
                {hasResolvedBirthPlace ? (
                  <p>
                    <strong>{t('accountCoordinates')}:</strong>{' '}
                    {t('mapaAstralCoordinates', {
                      lat: form.birthLatitude?.toFixed(4) ?? '',
                      lon: form.birthLongitude?.toFixed(4) ?? '',
                    })}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <p className="font-semibold">{t('accountBirthRecordImpactTitle')}</p>
              <div className="mt-2 space-y-2">
                <p>{t('accountBirthRecordImpactNatalChart')}</p>
                <p>{t('accountBirthRecordImpactDaily')}</p>
                <p>{t('accountBirthRecordImpactWeekly')}</p>
                <p>{t('accountBirthRecordImpactMonthly')}</p>
              </div>
            </div>

            {insufficientCreditsMessage ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                {insufficientCreditsMessage}
              </div>
            ) : null}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault()
                void handleConfirmSubmission()
              }}
              className="bg-slate-900 text-white hover:bg-slate-800"
              disabled={!canReviewChanges || !hasSufficientCredits || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('accountBirthRecordConfirmSubmitting')}
                </>
              ) : (
                t('accountBirthRecordConfirmAction')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </IonPage>
  )
}

export default BirthRecordUpdatePage
